import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { AuthError, AuthApiError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { trackUserAction } from "@/utils/tracking";
import { toast } from "sonner";
import { Footer } from "@/components/ui/footer";

const Login = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (session) {
          navigate("/");
        }
      } catch (error) {
        console.error("Session check error:", error);
        if (error instanceof AuthApiError) {
          setErrorMessage(getErrorMessage(error));
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN') {
        await trackUserAction('login');
        toast.success("Welcome back!");
        navigate("/");
      } else if (event === 'SIGNED_OUT') {
        setErrorMessage("");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const getErrorMessage = (error: AuthError) => {
    if (error instanceof AuthApiError) {
      switch (error.status) {
        case 400:
          if (error.message.includes('Email not confirmed')) {
            return 'Please verify your email address before signing in.';
          }
          return 'Invalid email or password. Please check your credentials.';
        case 422:
          return 'Invalid email format. Please enter a valid email address.';
        case 429:
          return 'Too many login attempts. Please try again later.';
        default:
          return error.message;
      }
    }
    return 'An unexpected error occurred. Please try again.';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="mb-6 text-center">
              <img 
                src="/lovable-uploads/2e8b9aef-e4ee-4b5a-a5f1-d90ec82327a9.png" 
                alt="Supplyfied Logo" 
                className="h-16 w-auto mx-auto mb-4"
              />
              <h1 className="text-2xl font-bold text-primary">Welcome Back</h1>
              <p className="text-muted-foreground">Sign in to access your data room</p>
            </div>

            {errorMessage && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            <Auth
              supabaseClient={supabase}
              appearance={{
                theme: ThemeSupa,
                variables: {
                  default: {
                    colors: {
                      brand: '#00BFA6',
                      brandAccent: '#00A693',
                    },
                  },
                },
                style: {
                  button: {
                    borderRadius: '6px',
                    height: '40px',
                  },
                  input: {
                    borderRadius: '6px',
                    height: '40px',
                  },
                  container: {
                    gap: '16px',
                  },
                  message: {
                    color: 'red',
                    marginBottom: '8px',
                  },
                },
              }}
              providers={[]}
              view="sign_in"
              showLinks={false}
            />
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default Login;
